<script setup lang="ts">
import { defineProps, computed } from 'vue';
import useStickyAlerts from '@/modules/alerts/composables/useStickyAlerts';
import {
  BasicStickyAlertPropsSchema,
  BASIC_STICKY_ALERT_TYPE,
  BasicStickyAlertType,
} from '@/modules/alerts/models/basic-sticky-alert-props';
import { STICKY_ALERT_TYPE } from '@/modules/alerts/models/sticky-alert-type';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const { findAlert, answerAlert } = useStickyAlerts();

const props = defineProps<BasicStickyAlertPropsSchema>();

const alert = findAlert(STICKY_ALERT_TYPE.BASIC, props.id);

interface TypeBasedProperties {
  foregroundColorClass: string;
  backgroundColorClass: string;
  icon: string;
}

const propertyMap: Record<BasicStickyAlertType, TypeBasedProperties> = {
  [BASIC_STICKY_ALERT_TYPE.SUCCESS]: {
    foregroundColorClass: 'text-success',
    backgroundColorClass: 'bg-success',
    icon: 'circle-check',
  },
  [BASIC_STICKY_ALERT_TYPE.WARNING]: {
    foregroundColorClass: 'text-warning',
    backgroundColorClass: 'bg-warning',
    icon: 'triangle-exclamation',
  },
  [BASIC_STICKY_ALERT_TYPE.ERROR]: {
    foregroundColorClass: 'text-danger',
    backgroundColorClass: 'bg-danger',
    icon: 'circle-exclamation',
  },
};

const foregroundColorClass = computed(() => propertyMap[props.type].foregroundColorClass);
const backgroundColorClass = computed(() => propertyMap[props.type].backgroundColorClass);
const icon = computed(() => propertyMap[props.type].icon);

const close = () => {
  answerAlert(alert, { result: 'DISMISS' });
};

if (!props.timeoutMs) {
  console.debug('timeoutMs is 0 (or falsy), not setting timeout.');
} else {
  console.debug(`Creating dismiss timeout for ${props.timeoutMs}ms from now.`);
  setTimeout(() => {
    const delayedAlert = findAlert(STICKY_ALERT_TYPE.BASIC, props.id);
    if (delayedAlert === null) {
      console.debug('Failed to find alert while timing out, it must have already been closed.');
      return;
    }
    answerAlert(alert, { result: 'TIME_OUT' });
  }, props.timeoutMs);
}
</script>

<template>
  <div class="accented-container">
    <div class="accent" :class="backgroundColorClass" data-testid="accent"></div>
    <div class="basic-alert d-flex justify-content-start align-items-center">
      <div class="px-2">
        <font-awesome-icon
          :icon="icon"
          size="lg"
          :class="foregroundColorClass"
          data-testid="icon"
        />
      </div>
      <span class="font-weight-bold flex-grow-1" data-testid="message">
        {{ props.message }}
      </span>
      <div class="px-2 cursor-pointer" @click="close" data-testid="closeButton">
        <font-awesome-icon icon="xmark" size="lg" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '~@/assets/styles/_tools.scss';
@import '~@/assets/styles/_settings.scss';

$border-radius: 0.5rem;

.accented-container {
  position: relative;
  width: 100%;
}

.accent {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: $border-radius;
  border-top-left-radius: $border-radius;
  border-bottom-left-radius: $border-radius;
}

.basic-alert {
  width: 100%;
  background-color: $body-bg;
  border-style: solid;
  border-width: 2px;
  border-color: color-contrast($body-bg);
  border-radius: $border-radius;
  padding: 0.25rem $border-radius;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
